import {
  ConnectivityState,
  PingKind,
  type ConnectivitySegment,
  type EquipmentAvailabilityDay,
} from "@eisox/api/core-api";
import dayjs from "@eisox/dayjs";

import { DATE_FORMAT } from "~/constants/date.constant";
import { EMPTY_VALUE } from "~/constants/display.constant";
import type { DayRange } from "~/helpers/day-range.helper";
import { getLocale } from "~/paraglide/runtime";

import { CONNECTIVITY_HISTORY_DEFAULT_DAYS, CONNECTIVITY_HISTORY_MAX_DAYS } from "../connectivity-history.constant";
import type { DayEvent, OutageState } from "../types/connectivity-history.type";

const DURATION_UNITS = [
  { unit: "day", ms: 24 * 60 * 60 * 1000 },
  { unit: "hour", ms: 60 * 60 * 1000, perParent: 24 },
  { unit: "minute", ms: 60 * 1000, perParent: 60 },
  { unit: "second", ms: 1000, perParent: 60 },
];

const OUTAGE_STATES: ConnectivityState[] = [ConnectivityState.DEGRADED, ConnectivityState.DISCONNECTED];

const isOutage = (segment: ConnectivitySegment): segment is ConnectivitySegment & { state: OutageState } =>
  OUTAGE_STATES.includes(segment.state);

const getConnectivityHistoryDefaultRange = (today: string): DayRange => ({
  from: dayjs(today)
    .subtract(CONNECTIVITY_HISTORY_DEFAULT_DAYS - 1, "day")
    .format(DATE_FORMAT),
  to: today,
});

const clampConnectivityHistoryRange = ({ from, to }: DayRange): DayRange => {
  const lastDay = dayjs(from)
    .add(CONNECTIVITY_HISTORY_MAX_DAYS - 1, "day")
    .format(DATE_FORMAT);
  return { from, to: to > lastDay ? lastDay : to };
};

const dayEvents = ({ segments, earlyPings }: EquipmentAvailabilityDay): DayEvent[] =>
  [
    ...segments.filter(isOutage).map(({ state, start, end }): DayEvent => ({ kind: state, start, end })),
    ...earlyPings.map((start): DayEvent => ({ kind: PingKind.EARLY, start })),
  ].sort((a, b) => dayjs(a.start).diff(b.start));

const dayProgress = ({ start, end }: EquipmentAvailabilityDay, instant: string): number =>
  dayjs(instant).diff(start) / dayjs(end).diff(start);

const formatDuration = (ms: number): string => {
  const roundedMs = Math.round(ms / 1000) * 1000;
  const parts = DURATION_UNITS.map(({ unit, ms: unitMs, perParent }) => {
    const value = Math.floor(roundedMs / unitMs);
    return { unit, value: perParent ? value % perParent : value };
  });
  const first = parts.findIndex(({ value }) => value > 0);
  const shown =
    first === -1 ? [{ unit: "second", value: 0 }] : parts.slice(first, first + 2).filter(({ value }) => value > 0);
  return shown
    .map(({ unit, value }) =>
      new Intl.NumberFormat(getLocale(), { style: "unit", unit, unitDisplay: "short" }).format(value),
    )
    .join(" ");
};

const formatDrift = (driftMs: number): string => {
  const sign = driftMs > 0 ? "+" : driftMs < 0 ? "−" : "";
  return `${sign}${formatDuration(Math.abs(driftMs))}`;
};

const formatAvailability = (availability: number | undefined): string =>
  availability === undefined
    ? EMPTY_VALUE
    : new Intl.NumberFormat(getLocale(), { style: "percent", maximumFractionDigits: 2 }).format(availability);

export {
  clampConnectivityHistoryRange,
  dayEvents,
  dayProgress,
  formatAvailability,
  formatDrift,
  formatDuration,
  getConnectivityHistoryDefaultRange,
};
